import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Star, MapPin, X } from "lucide-react";
import PayButton from "../components/PayButton";
import useGeo from "../hooks/useGeo";
import GeoToggle from "../components/GeoToggle";
import { track } from "../lib/analytics";

export default function Vets() {
  const [vets, setVets] = useState([]);
  const [pets, setPets] = useState([]);
  const [selectedVet, setSelectedVet] = useState(null);
  const [form, setForm] = useState({ pet_id: "", date_time: "", service: "General Checkup" });
  const [msg, setMsg] = useState("");
  const [paid, setPaid] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);
  const [bookDate, setBookDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const geo = useGeo(25);

  useEffect(() => {
    axios.get(`${API}/vets`, { params: geo.geoParams }).then(r => setVets(r.data));
    axios.get(`${API}/pets`).then(r => { setPets(r.data); setForm(f => ({...f, pet_id: r.data[0]?.pet_id || ""})); });
  }, [geo.mode, geo.coords]);

  useEffect(() => {
    if (!selectedVet || !bookDate) { setSlots([]); return; }
    setSlotsLoading(true);
    axios.get(`${API}/vets/${selectedVet.vet_id}/slots?date=${bookDate}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedVet, bookDate]);

  const book = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!form.date_time) { setMsg("Pick a time slot first."); return; }
    try {
      const r = await axios.post(`${API}/appointments`, {
        pet_id: form.pet_id, vet_id: selectedVet.vet_id, date_time: form.date_time, service: form.service,
      });
      track("vet_booked", { vet_id: selectedVet.vet_id, service: form.service });
      setBookedAppt(r.data);
    } catch (e) { setMsg("Could not book — please try again."); }
  };

  const closeAll = () => { setSelectedVet(null); setBookedAppt(null); setPaid(false); setMsg(""); setBookDate(""); setSlots([]); setForm(f => ({ ...f, date_time: "" })); };

  return (
    <div className="space-y-6" data-testid="vets-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-extrabold">Find a Vet</h1>
        <GeoToggle geo={geo} label="vets" />
      </div>
      {geo.isNearby && vets.length === 0 && (
        <div className="bg-surface-muted border border-line rounded-2xl p-6 text-center text-sm" data-testid="vets-empty-nearby">
          <div className="font-bold mb-1">No vets within {geo.radius} km of you yet.</div>
          <p className="text-ink-soft">New clinics are joining every week. Switch to <button onClick={() => geo.setMode("all")} className="underline font-bold">All India</button> to browse everyone.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {vets.map(v => (
          <article key={v.vet_id} data-testid={`vet-card-${v.vet_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
            <img src={v.photo || "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80"} alt={v.name} className="w-full h-44 object-cover" />
            <div className="p-5">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <h3 className="font-heading text-xl font-bold">{v.name}</h3>
                  <div className="text-sm text-ink-soft">{v.specialty}</div>
                </div>
                <div className="flex items-center gap-1 text-sm font-bold"><Star className="w-4 h-4 text-sunshine fill-sunshine"/>{v.rating}</div>
              </div>
              <div className="text-xs text-ink-soft mt-2 flex items-center gap-1"><MapPin className="w-3 h-3"/>{v.clinic} · {v.city}{v.distance_km != null && <span className="ml-1 font-bold text-brand">· {v.distance_km} km away</span>}</div>
              <p className="text-sm text-ink-soft mt-2 line-clamp-2">{v.bio}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-heading font-bold text-lg">₹{v.fee}</span>
                <button
                  data-testid={`book-vet-${v.vet_id}`}
                  onClick={() => setSelectedVet(v)}
                  className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-brand transition-colors"
                >Book</button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {selectedVet && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={closeAll}>
          <div onClick={e=>e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="book-vet-form">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-2xl font-extrabold">Book {selectedVet.name}</h3>
                <p className="text-sm text-ink-soft">{selectedVet.clinic}</p>
              </div>
              <button type="button" onClick={closeAll} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>

            {!bookedAppt && (
              <form onSubmit={book} className="space-y-3">
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Pet</span>
                  <select data-testid="book-pet-select" required value={form.pet_id} onChange={e=>setForm({...form, pet_id:e.target.value})} className="input">
                    {pets.map(p => <option key={p.pet_id} value={p.pet_id}>{p.name} · {p.breed}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Date</span>
                  <input data-testid="book-date" type="date" required min={new Date().toISOString().slice(0,10)} value={bookDate} onChange={e=>{setBookDate(e.target.value); setForm({...form, date_time: ""});}} className="input"/>
                </label>
                {bookDate && (
                  <div data-testid="slots-grid">
                    <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-1.5">Available slots</div>
                    {slotsLoading ? <div className="text-xs text-ink-soft">Loading…</div> : slots.length === 0 ? <div className="text-xs text-ink-soft">No slots available — try another date.</div> : (
                      <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto pr-1">
                        {slots.map(s => {
                          const time = s.slice(11);
                          const selected = form.date_time === s;
                          return <button type="button" key={s} data-testid={`slot-${time}`} onClick={() => setForm({...form, date_time: s})} className={`rounded-full py-1.5 text-xs font-bold border ${selected ? "bg-brand text-white border-brand" : "bg-surface border-line hover:bg-surface-muted"}`}>{time}</button>;
                        })}
                      </div>
                    )}
                  </div>
                )}
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Service</span>
                  <select data-testid="book-service" value={form.service} onChange={e=>setForm({...form, service:e.target.value})} className="input">
                    {["General Checkup","Vaccination","Deworming","Grooming Consult","Dental","Allergy"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </label>
                {msg && <div className="text-sm font-bold text-danger">{msg}</div>}
                <button data-testid="confirm-booking" className="w-full bg-ink text-white rounded-full py-3 font-bold hover:bg-brand transition-colors">
                  Confirm booking · then pay ₹{selectedVet.fee}
                </button>
              </form>
            )}

            {bookedAppt && !paid && (
              <div className="space-y-3" data-testid="vet-pay-step">
                <div className="bg-leaf/30 rounded-2xl p-3 text-sm font-bold">✓ Appointment held for {bookedAppt.pet_name} on {new Date(bookedAppt.date_time).toLocaleString()}.</div>
                <p className="text-sm text-ink-soft">Pay the consultation fee to lock your slot.</p>
                <PayButton
                  amount={selectedVet.fee}
                  currency="INR"
                  country="IN"
                  purpose="vet_booking"
                  referenceId={bookedAppt.appointment_id}
                  label={`Pay ₹${selectedVet.fee}`}
                  dataTestid="vet-pay-button"
                  onSuccess={() => setPaid(true)}
                />
              </div>
            )}

            {bookedAppt && paid && (
              <div className="text-center py-6" data-testid="vet-pay-done">
                <Star className="w-12 h-12 text-sunshine mx-auto mb-2 fill-sunshine"/>
                <div className="font-heading text-xl font-extrabold">Slot confirmed!</div>
                <p className="text-sm text-ink-soft mt-1">See you on {new Date(bookedAppt.date_time).toLocaleString()}.</p>
                <button onClick={closeAll} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
      <style>{`.input{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;color:#2C2C2C;outline:none;}.input:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}
